/**
 * V210 — cuerpo del aviso: el DOCUMENTO PROPIO del tenant, igual en la página pública y en
 * la vista previa del panel. .docx = HTML ya convertido y sanitizado en el servidor (lista
 * blanca, sin scripts/estilos/`javascript:`); PDF = incrustado; otro = solo descarga.
 * Nunca pinta texto de plantilla ni el recuadro "Plantilla base".
 */
import type { AvisoDocumentKind } from './avisoDocument';

interface Props {
  kind: AvisoDocumentKind;
  /** HTML sanitizado del .docx (null si es PDF o si la conversión falló). */
  html: string | null;
  /** URL (blob: o pública) del PDF para incrustarlo. */
  pdfSrc?: string | null;
  fileName: string;
  /** Descarga del original (enlace directo o handler cuando requiere sesión). */
  downloadHref?: string;
  onDownload?: () => void;
}

// Tipografía legible en celular para el HTML del documento (Tailwind arbitrary variants;
// las tablas largas se desplazan dentro de su contenedor, nunca la página).
const DOC_CLS = [
  'aviso-doc text-sm leading-relaxed text-slate-700 break-words',
  '[&_h1]:mt-5 [&_h1]:font-serif [&_h1]:text-xl [&_h1]:text-slate-900',
  '[&_h2]:mt-5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-slate-900',
  '[&_h3]:mt-4 [&_h3]:font-semibold [&_h3]:text-slate-800',
  '[&_p]:mt-2 [&_ul]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mt-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:mt-1 [&_a]:text-sky-700 [&_a]:underline',
  '[&_table]:mt-3 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:border-collapse',
  '[&_td]:border [&_td]:border-slate-200 [&_td]:p-2 [&_td]:align-top [&_th]:border [&_th]:border-slate-200 [&_th]:p-2',
  '[&_td_p]:mt-0 [&_th_p]:mt-0',
].join(' ');

export function AvisoDocumentView({ kind, html, pdfSrc, fileName, downloadHref, onDownload }: Props) {
  const download = downloadHref ? (
    <a href={downloadHref} target="_blank" rel="noreferrer"
      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
      Descargar el documento original <span className="truncate text-[11px] text-slate-400">{fileName}</span>
    </a>
  ) : onDownload ? (
    <button type="button" onClick={onDownload}
      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50">
      Descargar el documento original <span className="truncate text-[11px] text-slate-400">{fileName}</span>
    </button>
  ) : null;

  return (
    <div>
      {kind === 'docx' && html ? (
        <div className={DOC_CLS} data-testid="aviso-documento" dangerouslySetInnerHTML={{ __html: html }} />
      ) : kind === 'pdf' && pdfSrc ? (
        <object data={pdfSrc} type="application/pdf" aria-label={`Aviso de privacidad: ${fileName}`}
          className="h-[70vh] w-full rounded-xl border border-slate-200">
          <p className="p-4 text-sm text-slate-600">Tu navegador no puede mostrar el PDF aquí. Descárgalo abajo.</p>
        </object>
      ) : (
        <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          El aviso de privacidad completo está en el documento del negocio. Descárgalo para leerlo.
        </p>
      )}
      {download && <div className="mt-4">{download}</div>}
    </div>
  );
}
